import api, { handleApiError } from "./api";
import { AxiosError } from "axios";

export type EsignProvider = "docusign" | "docuseal";

export type ContractEmailProvider = "gmail" | "outlook";

/** Path segment used by the OAuth start/callback endpoints -- distinct from
 * ContractEmailProvider ("gmail"/"outlook", the stored provider values). */
export type ContractEmailOAuthProvider = "google" | "microsoft";

/** Keys of the org-customizable automated emails (mirrors the backend). */
export type AutomatedEmailTemplateKey = "approved" | "declined" | "funded" | "accounting";

export interface AutomatedEmailTemplate {
  subject: string;
  body: string;
}

export interface AutomatedEmailVariable {
  name: string;
  description: string;
  /** Expands to a pre-built HTML section (offer grid, positions, ...). */
  block: boolean;
}

/** Org-level party pickers (mirrors ``apps.merchant_flow.stakeholders.RECIPIENT_PICKERS``). */
export type RecipientPickerKey = "documents";

export interface RecipientParty {
  key: string;
  label: string;
  /** Always receives the email: shown on and disabled, rejected if turned off. */
  locked: boolean;
}

export interface RecipientPicker {
  /** Effective choices: defaults, then saved overrides, locked parties on. */
  value: Record<string, boolean>;
  defaults: Record<string, boolean>;
  parties: RecipientParty[];
  /** Settings note on which parties apply where. */
  hint?: string;
}

export interface DocumentsEmailPreview {
  audience: string;
  label: string;
  subject: string;
  html: string;
  attachments: string[];
}

export type UnderwritingRangeValue = { min: string | number; max: string | number };

export type UnderwritingRuleValue = string | number | boolean | UnderwritingRangeValue;

export function isUnderwritingRangeValue(value: unknown): value is UnderwritingRangeValue {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    "min" in value &&
    "max" in value
  );
}

export interface UnderwritingRuleSchemaItem {
  id: string;
  label: string;
  description: string;
  type: "percent" | "money" | "int" | "factor" | "rate" | "days" | "bool" | "enum" | "range";
  min: string | number | null;
  max: string | number | null;
  step: string | number | null;
  enum: string[] | null;
  suffix: string | null;
  group: string;
  consumers: string[];
  endpoint_type?: "percent" | "money" | "int" | "factor" | "rate" | "days" | null;
  endpoint_max?: (string | number | null)[] | null;
}

export interface UnderwritingRulesPayload {
  schema: UnderwritingRuleSchemaItem[];
  values: Record<string, UnderwritingRuleValue>;
  defaults: Record<string, UnderwritingRuleValue>;
}

export interface OrganizationSettings {
  name: string;
  brand_color: string;
  contact_email: string;
  docusign_template_id: string;
  esign_provider: EsignProvider;
  signed_contract_copy_emails: string;
  contract_email_address: string;
  contract_email_provider: ContractEmailProvider;
  contract_email_connected: boolean;
  contract_email_connected_at: string | null;
  contract_email_reconnect_required: boolean;
  submissions_inbox_address: string;
  submissions_inbox_provider: ContractEmailProvider;
  submissions_inbox_connected: boolean;
  submissions_inbox_connected_at: string | null;
  submissions_inbox_reconnect_required: boolean;
  /** Apply Processing / Ready / Funded / Declined on the connected mailbox. */
  submissions_inbox_label_mail: boolean;
  /** Mark handled mail read. Off until an admin turns it on. */
  submissions_inbox_mark_read: boolean;
  org_code: string;
  inbox_address: string;
  /** Warn when a send omits the pre-engagement disclosure in a state that requires one. */
  disclosure_warning_enabled: boolean;
  /**
   * When on, the approval modal offers Approve + Send Contract alongside Approve.
   * When off, approval only moves the deal to Contract Ready.
   */
  auto_send_contracts_enabled: boolean;
  /** When off, underwriters cannot start Merchant Tasks / send contracts. */
  underwriters_can_send_merchant_tasks: boolean;
  /** When off, brokers cannot start Merchant Tasks / send contracts. */
  brokers_can_send_merchant_tasks: boolean;
  /**
   * When on, offer-calc Term is always days. When off, Term display matches
   * frequency (days / weeks / months); stored values remain business days.
   */
  offer_term_always_days: boolean;
  /** Master switch: nothing below sends while this is off. */
  automated_emails_enabled: boolean;
  decision_emails_enabled: boolean;
  funded_emails_enabled: boolean;
  accounting_emails_enabled: boolean;
  accounting_emails: string[];
  /** Effective templates (saved override or default, field by field). */
  automated_email_templates: Record<AutomatedEmailTemplateKey, AutomatedEmailTemplate>;
  automated_email_defaults: Record<AutomatedEmailTemplateKey, AutomatedEmailTemplate>;
  automated_email_variables: Record<AutomatedEmailTemplateKey, AutomatedEmailVariable[]>;
  automated_email_recipients: Record<AutomatedEmailTemplateKey, string>;
  automated_email_pickers: Record<RecipientPickerKey, RecipientPicker>;
  /** Decimal string, e.g. "10.00". */
  default_commission_percentage: string;
  /** Decimal string for refinance offers, e.g. "10.00". */
  default_refi_commission_percentage: string;
  underwriting_rules: UnderwritingRulesPayload;
  /** The assistant model ref that applies: the organization's pick, else the platform default. */
  agent_default_model: string | null;
  /** Days an idle assistant chat is kept; null keeps them forever. */
  chat_retention_days: number | null;
  /** The model picker shows each user today's AI use (a percent of the daily limit). */
  agent_show_usage: boolean;
  /** Whether the assistant on WhatsApp and Telegram sees what users had open on the web app. */
  agent_desktop_context: boolean;
  /** Trial account: settings below are locked to defaults (server rejects PATCH with 403). */
  is_trial: boolean;
  trial_started_at: string | null;
  /** Field names the server refuses to change while `is_trial` is on. Empty for full accounts. */
  trial_locked_fields: string[];
  /** DocuSeal template seeded as the locked default contract; null for full accounts. */
  trial_default_contract_template_id: number | null;
}

/** Safe read of one catalog value; GET payloads may omit `underwriting_rules`. */
export function underwritingRuleValue(
  settings: OrganizationSettings | undefined | null,
  id: string
): string | number | boolean | UnderwritingRangeValue | undefined {
  return settings?.underwriting_rules?.values?.[id];
}

/** Catalog default when the overlay omits the id or stores a blank; 0 stays a real override. */
export function resolveUnderwritingNumber(raw: unknown, fallback: number): number {
  if (raw == null || raw === "") return fallback;
  const n = Number(raw);
  return Number.isFinite(n) ? n : fallback;
}

export interface UpdateOrganizationSettingsRequest {
  name?: string;
  brand_color?: string;
  contact_email?: string;
  docusign_template_id?: string;
  esign_provider?: EsignProvider;
  signed_contract_copy_emails?: string;
  automated_emails_enabled?: boolean;
  submissions_inbox_label_mail?: boolean;
  submissions_inbox_mark_read?: boolean;
  decision_emails_enabled?: boolean;
  funded_emails_enabled?: boolean;
  accounting_emails_enabled?: boolean;
  accounting_emails?: string[];
  /** Merged per key on the server: only the keys sent are replaced. */
  automated_email_templates?: Partial<
    Record<AutomatedEmailTemplateKey, Partial<AutomatedEmailTemplate>>
  >;
  /** Merged per picker and party on the server; locked parties cannot be turned off. */
  automated_email_parties?: Partial<Record<RecipientPickerKey, Record<string, boolean>>>;
  default_commission_percentage?: string | number;
  default_refi_commission_percentage?: string | number;
  /** Partial merge of registered variable overrides. */
  underwriting_rules?: Record<string, UnderwritingRuleValue>;
  disclosure_warning_enabled?: boolean;
  auto_send_contracts_enabled?: boolean;
  underwriters_can_send_merchant_tasks?: boolean;
  brokers_can_send_merchant_tasks?: boolean;
  offer_term_always_days?: boolean;
  /** A ref from GET agent/models/; null goes back to the platform default. */
  agent_default_model?: string | null;
  /** Null, or 1 to 3650 days. */
  chat_retention_days?: number | null;
  agent_show_usage?: boolean;
  agent_desktop_context?: boolean;
}

export interface ContractEmailOAuthStartResponse {
  authorization_url: string;
}

export interface AutomatedEmailPreviewRequest {
  key: AutomatedEmailTemplateKey;
  /** Blank fields preview the built-in default, like the send path. */
  subject: string;
  body: string;
}

export interface AutomatedEmailPreviewResponse {
  subject: string;
  /** Full email HTML rendered against a sample deal. */
  html: string;
}

export const organizationSettingsService = {
  get: async (): Promise<OrganizationSettings> => {
    const { data } = await api.get<OrganizationSettings>(`/accounts/organization-settings/`);
    return data;
  },

  update: async (payload: UpdateOrganizationSettingsRequest): Promise<OrganizationSettings> => {
    const { data } = await api.patch<OrganizationSettings>(
      `/accounts/organization-settings/`,
      payload
    );
    return data;
  },

  /**
   * Fetches the provider's consent screen URL (an authenticated call, JWT
   * header attached). The caller then does `window.location.href =
   * authorization_url` to navigate the browser there directly -- a raw
   * top-level navigation straight to a Steddy URL would carry no auth header.
   */
  startContractEmailOAuth: async (
    provider: ContractEmailOAuthProvider
  ): Promise<ContractEmailOAuthStartResponse> => {
    const { data } = await api.get<ContractEmailOAuthStartResponse>(
      `/accounts/organization-settings/oauth/${provider}/start/`
    );
    return data;
  },

  startSubmissionsInboxOAuth: async (
    provider: ContractEmailOAuthProvider
  ): Promise<ContractEmailOAuthStartResponse> => {
    const { data } = await api.get<ContractEmailOAuthStartResponse>(
      `/accounts/organization-settings/oauth/${provider}/start/`,
      { params: { purpose: "submissions" } }
    );
    return data;
  },

  previewAutomatedEmail: async (
    payload: AutomatedEmailPreviewRequest
  ): Promise<AutomatedEmailPreviewResponse> => {
    const { data } = await api.post<AutomatedEmailPreviewResponse>(
      `/accounts/organization-settings/automated-emails/preview/`,
      payload
    );
    return data;
  },

  /** The signed-documents email as each audience receives it, with sample data. */
  previewDocumentsEmails: async (): Promise<DocumentsEmailPreview[]> => {
    const { data } = await api.get<{ previews: DocumentsEmailPreview[] }>(
      `/accounts/organization-settings/automated-emails/preview/documents/`
    );
    return data.previews;
  },

  disconnectContractEmail: async (): Promise<OrganizationSettings> => {
    const { data } = await api.post<OrganizationSettings>(
      `/accounts/organization-settings/oauth/disconnect/`
    );
    return data;
  },

  disconnectSubmissionsInbox: async (): Promise<OrganizationSettings> => {
    const { data } = await api.post<OrganizationSettings>(
      `/accounts/organization-settings/oauth/submissions/disconnect/`
    );
    return data;
  },
};

export const getOrganizationSettingsErrorMessage = (error: unknown): string => {
  if (error instanceof AxiosError) return handleApiError(error);
  if (error instanceof Error) return error.message;
  return "An unexpected error occurred";
};
